import { generateMnemonic, mnemonicToSeed } from '@exodus/bip39'
import { safeString } from '@exodus/safe-string'
import { getSeedId } from '@exodus/key-utils'
import assert from 'minimalistic-assert'
import makeConcurrent from 'make-concurrent'

import { assertMnemonic, genPassphrase } from './utils.js'

const SEED_KEY = 'seed'
// Legacy multi-seed storage key. The feature is gone, but installs that previously
// added an extra seed may still have an encrypted payload here — clear() must wipe it.
const LEGACY_EXTRA_SEEDS_KEY = 'extraSeeds'
const GENERATED_PASSPHRASE_KEY = 'generatedPassphrase'
const HAS_USER_SET_PASSPHRASE_KEY = 'hasUserSetPassphrase'

class Wallet {
  #keychain
  #primarySeedIdAtom
  #seedMetadataAtom
  #isLocked = true
  #clock
  #logger
  #usePassword
  #useAutoGeneratedPassword
  #validMnemonicLengths

  constructor({
    keychain,
    primarySeedIdAtom,
    seedMetadataAtom,
    seedStorage,
    unsafeStorage,
    config,
    logger,
    clock = { now: () => Date.now() },
  }) {
    assert(keychain, 'missing keychain')
    assert(
      Array.isArray(config.validMnemonicLengths),
      'validMnemonicLengths has to be an array of integers'
    )
    config.validMnemonicLengths.map((length) =>
      assert(Number.isInteger(length), 'Invalid mnemonic length')
    )

    this.#clock = clock
    this.#logger = logger
    this.#keychain = keychain
    this.#primarySeedIdAtom = primarySeedIdAtom
    this.#seedMetadataAtom = seedMetadataAtom
    this.#usePassword = config.usePassword
    this.#useAutoGeneratedPassword = config.useAutoGeneratedPassword
    this.#validMnemonicLengths = config.validMnemonicLengths
    this.locked = true

    // TODO: Pass namespaced storage instance to wallet module
    this.walletStorage = seedStorage.namespace('wallet')
    this.flagsStorage = unsafeStorage.namespace('flags')
  }

  exists = async () => {
    const encryptedSeed = await this.walletStorage.get(SEED_KEY)
    return !!encryptedSeed
  }

  hasPassphraseSet = async () => {
    if (this.#usePassword) {
      const walletExists = await this.exists()

      // need to fallback to generatedPassphrase for backwards compatibility
      const hasPassphrase =
        (await this.walletStorage.get(HAS_USER_SET_PASSPHRASE_KEY)) ??
        !(await this.#getGeneratedPassphrase())

      return walletExists && hasPassphrase
    }

    return false
  }

  #getSeed = async ({ passphrase }) => {
    if (this.#usePassword && !passphrase && this.#useAutoGeneratedPassword) {
      passphrase = await this.#getGeneratedPassphrase()
    }

    return this.walletStorage.get(SEED_KEY, { passphrase })
  }

  #setSeed = async ({ seed, passphrase }) => {
    const userProvidedPassphrase = Boolean(passphrase)
    if (this.#usePassword && !passphrase) {
      if (this.#useAutoGeneratedPassword) {
        passphrase = genPassphrase()
        await this.walletStorage.set(GENERATED_PASSPHRASE_KEY, passphrase)
      }

      await this.walletStorage.set(HAS_USER_SET_PASSPHRASE_KEY, false)
    }

    await this.walletStorage.set(SEED_KEY, seed, { passphrase })
    // Restoring a seedless backup restarts the app immediately.
    // Wait a bit longer to ensure the data is fully stored before restarting.
    const storedSeed = await this.walletStorage.get(SEED_KEY, { passphrase })
    assert(storedSeed.seed.equals(seed.seed), safeString`setSeed failed`)

    if (this.#usePassword && userProvidedPassphrase) {
      await this.walletStorage.set(HAS_USER_SET_PASSPHRASE_KEY, true)
      await this.walletStorage.delete(GENERATED_PASSPHRASE_KEY)
    }
  }

  #getGeneratedPassphrase = async () => this.walletStorage.get(GENERATED_PASSPHRASE_KEY)

  create = makeConcurrent(
    async ({ mnemonic, passphrase } = {}) => {
      mnemonic = mnemonic || (await generateMnemonic({ bitsize: 128 }))

      const dateCreated = this.#clock.now()
      const seedBuffer = await mnemonicToSeed({ mnemonic, format: 'buffer', validate: false })
      const seed = { mnemonic, seed: seedBuffer, dateCreated }
      const seedId = await getSeedId(seedBuffer)

      await this.#setSeed({ seed, passphrase })

      this.#seedMetadataAtom.set((previous) => ({
        ...previous,
        [seedId]: { dateCreated },
      }))

      return { seedId }
    },
    { concurrency: 1 }
  )

  import = makeConcurrent(
    async ({ mnemonic, passphrase }) => {
      await assertMnemonic(mnemonic, this.#validMnemonicLengths)

      return this.create({ passphrase, mnemonic })
    },
    { concurrency: 1 }
  )

  clear = async () => {
    this.lock()

    // Avoid using this.walletStorage.clear as it's not implemented in mobile
    await Promise.all([
      this.walletStorage.delete(SEED_KEY),
      this.walletStorage.delete(LEGACY_EXTRA_SEEDS_KEY),
      this.walletStorage.delete(GENERATED_PASSPHRASE_KEY),
      this.walletStorage.delete(HAS_USER_SET_PASSPHRASE_KEY),
      this.#seedMetadataAtom.set(undefined),
    ])
  }

  isLocked = async () => this.#isLocked

  lock = async () => {
    this.#keychain.removeAllSeeds()

    this.#isLocked = true
  }

  unlock = async ({ passphrase } = {}) => {
    try {
      const { seed } = await this.#getSeed({ passphrase })
      const primarySeedId = await this.#keychain.addSeed(seed)
      this.#primarySeedIdAtom.set(primarySeedId)

      this.#isLocked = false

      return { primarySeedId }
    } catch (err) {
      this.#logger.debug('unlock() failed: wrong password', err)
      throw new Error('Wrong password. Try again.')
    }
  }

  getMnemonic = async ({ passphrase } = {}) => {
    let seed
    try {
      seed = await this.#getSeed({ passphrase })
    } catch (err) {
      this.#logger.debug('getMnemonic() failed: wrong password', err)
      throw new Error('Wrong password. Try again.')
    }

    if (!seed?.mnemonic) throw new Error('Wrong password. Try again.')
    return seed.mnemonic
  }

  changePassphrase = async ({ currentPassphrase, newPassphrase }) => {
    if (this.#usePassword) {
      let seed

      try {
        seed = await this.#getSeed({ passphrase: currentPassphrase })
      } catch {
        this.#logger.debug('changePassphrase() failed: wrong password')
        throw new Error('Wrong password. Try again.')
      }

      try {
        await this.#setSeed({ seed, passphrase: newPassphrase })
      } catch {
        this.#logger.debug('changePassphrase() failed')
        throw new Error('Something went wrong. Try again.')
      }
    } else {
      throw new Error('Password support is disabled')
    }
  }

  getSeedMetadata = () => this.#seedMetadataAtom.get()

  getPrimarySeedId = () => this.#primarySeedIdAtom.get()
}

const createWallet = (args = {}) => new Wallet({ ...args })

const walletDefinition = {
  id: 'wallet',
  type: 'module',
  factory: createWallet,
  dependencies: [
    'keychain',
    'primarySeedIdAtom',
    'seedStorage',
    'unsafeStorage',
    'config',
    'logger',
    'seedMetadataAtom',
    'clock?',
  ],
  public: true,
}

export default walletDefinition
